import { Centrifuge } from 'centrifuge';
import { HeaderSuport } from 'components/HeaderSuport/HeaderSuport';
import { SidebarSupportLeft } from 'components/SidebarSupportLeft/SidebarSupportLeft';
import { SidebarSupportRight } from 'components/SidebarSupportRight/SidebarSupportRight';
import { Loader } from 'layouts/Loader/Loader';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { setStatus, setTabSupport } from 'redux/reducers/app/reducer';
import { getTabNameSupport } from 'redux/reducers/app/selectors';
import { getIsAuth, getToken } from 'redux/reducers/auth/selectors';
import {
  getNotesRequest,
  getUsersRequest,
  onConect,
  removeListChats,
  setMassage,
  subscribeAllRequest,
  ticketsRequest,
} from 'redux/reducers/support/reducer';
import { geTticketActive, getUsersSelector } from 'redux/reducers/support/selectors';
import { IUsersRequest } from 'redux/reducers/support/types';
import { getUserRequest } from 'redux/reducers/users/reducer';
import { getUserDetails } from 'redux/reducers/users/selectors';
import { ELinksName } from 'services/constants/list-links-sidebar';
import { AnyRec, getActiveChatId, hasProp, normalizePayloadFromRes } from 'untils/centrifuge';
import { extractForward } from 'untils/extractForward';
import { getTokenRefreshFatch } from 'untils/getTokenFatch';
import { IsJsonString } from 'untils/isStringJson';
import { onJsonParseBigin } from 'untils/jsonParse';
import { onHttpCentrifuge } from 'untils/onHttp';

type ContextType = {
  centrifuge: any;
  getTicketOpen: (num: boolean) => void;
};

interface IMassage {
  channel: string;
  data: {
    admin: boolean;
    input: string;
    admin_id: number;
    time: number;
  };
}

const PAGE_SIZE = 15;

const SuportLayout: FC = () => {
  const dispatch = useDispatch();
  const isAuth = useSelector(getIsAuth);
  const location = useLocation();
  const [openUser, setOpenUser] = useState<number>(0);
  const [hideLeft, setHideLeft] = useState(false);
  const [hideRight, setHideRight] = useState(false);
  const users = useSelector(getUsersSelector); // очікую users.data, users.total
  const [filter, setFilter] = useState<IUsersRequest>({});
  const token = useSelector(getToken);
  const nameTabSupport = useSelector(getTabNameSupport);
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const [id_user, setId_user] = useState<number>(0);
  const userData = useSelector(getUserDetails);
  const [ticketOpen, setTicketOpen] = useState<boolean>(false);
  const ticket = useSelector(geTticketActive);

  // стабільний прапорець завантаження
  const loadingRef = useRef(false);

  // скрол-контейнер і sentinel
  const scrollRef = useRef<HTMLUListElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // для відстеження росту масиву
  const prevLoadedRef = useRef(0);

  // ДЕ-ДУПЛІКАЦІЯ мережевих запитів: key = queryKey|page
  const lastRequestKeyRef = useRef<string | null>(null);

  // UI прапорці
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [bootstrapped, setBootstrapped] = useState(false);

  // підрахунок
  const total = users?.total ?? 0;
  const loadedCount = users?.data?.length ?? 0;
  const noMoreBySize = loadedCount > 0 && loadedCount % PAGE_SIZE !== 0;
  const hasMore = total ? loadedCount < total : !noMoreBySize;

  const centrifuge = useMemo(
    () =>
      new Centrifuge(onHttpCentrifuge() + 'wsapp', {
        token: token || localStorage.getItem('token') || '',
        getToken: getTokenRefreshFatch || '',
      }),
    [token],
  );

  const getTicketOpen = (num: boolean) => setTicketOpen(num);

  // helper: санітизація фільтра
  const sanitizeFilter = useCallback((f: IUsersRequest) => {
    const clean: IUsersRequest = { ...f };
    if (!clean.country_code) {
      delete clean.country_code;
      delete clean.country_name;
    }
    if (!clean.type_id) delete clean.type_id;
    return clean;
  }, []);

  // helper: побудова ключа фільтрів без page/per_page
  const buildQueryKey = useCallback((req: IUsersRequest | undefined) => {
    if (!req) return '∅';
    const {
      asc,
      country_code,
      country_name,
      type_id,
      status,
      search,
      order_by,
      resolve,
      resolver_id,
      date_from,
      date_to,
      timeFrom,
      timeTo,
      user_id,
    } = req;
    const normDate = (d?: string | Date) => (d ? new Date(d).toISOString().slice(0, 10) : null);
    return JSON.stringify({
      asc: !!asc,
      country_code: country_code || null,
      country_name: country_name || null,
      type_id: type_id ?? null,
      status: status ?? null, // all=null, new=0, in progress=1, completed=2
      search: (search ?? '').trim().toLowerCase(),
      order_by: order_by || null,
      resolve: resolve ?? null,
      resolver_id: resolver_id ?? null,
      date_from: normDate(date_from),
      date_to: normDate(date_to),
      timeFrom: timeFrom || null,
      timeTo: timeTo || null,
      user_id: user_id ?? null,
    });
  }, []);

  const cleanFilter = useMemo(() => sanitizeFilter(filter), [filter, sanitizeFilter]);
  const queryKey = useMemo(() => buildQueryKey(cleanFilter), [cleanFilter, buildQueryKey]);
  const prevQueryKeyRef = useRef<string | null>(null);

  // запит сторінки (єдине місце) + ДЕ-ДУП
  const loadPage = useCallback(
    (nextPage: number) => {
      const reqKey = `${queryKey}|${nextPage}`;
      if (lastRequestKeyRef.current === reqKey) return; // <-- не дублюємо той самий запит
      lastRequestKeyRef.current = reqKey;

      if (loadingRef.current) return;
      loadingRef.current = true;
      setIsLoading(true);
      dispatch(getUsersRequest({ ...cleanFilter, current_page: nextPage, per_page: PAGE_SIZE }));
    },
    [dispatch, cleanFilter, queryKey],
  );

  // 1) підключення Centrifuge
  useEffect(() => {
    if (isAuth && token) {
      centrifuge.connect();
      centrifuge.on('connected', (ctx: { client: string }) => {
        dispatch(onConect(ctx.client));
        dispatch(subscribeAllRequest());
      });
    }
    return () => centrifuge.disconnect();
  }, [isAuth, token]);

  // 2) WebSocket публікації
  useEffect(() => {
    if (!(isAuth && token)) return;

    const handler = (message: IMassage) => {
      const raw = IsJsonString(message?.data?.input)
        ? onJsonParseBigin(message?.data?.input)
        : message?.data?.input;

      // перезавантаження списку (типова сист.подія)
      if (hasProp(raw, 'in') && hasProp(raw, 'item')) {
        setPage(1);
        loadingRef.current = false;
        setIsLoading(false);
        setBootstrapped(false);
        prevLoadedRef.current = 0;
        setInitialLoading(true);
        lastRequestKeyRef.current = null;
        scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
        loadPage(1);
      }

      // обмежимося каналом service
      if (message.channel !== 'service') return;

      // чи ця подія адресована саме цьому адмін-аккаунту?
      const isForThisAdmin = message?.data?.admin_id
        ? message.data.admin_id === userData?.data?.id
        : hasProp(raw, 'in') && (raw.in as AnyRec)?.userId === userData?.data?.id;

      const activeId = getActiveChatId();

      // === 1) кейс: пише АДМІН → простий об'єкт { admin_id, user_id, input } ===
      if (message?.data?.admin_id) {
        const userIdTarget = (message as AnyRec)?.data?.user_id as number | undefined;
        if (activeId && userIdTarget && userIdTarget !== activeId) return;
        // тут нічого не диспатчимо — логіка як у тебе
        return;
      }

      // === 2) кейс: пише КОРИСТУВАЧ → складний об'єкт з outMessage ===
      const res: any = onJsonParseBigin(message?.data?.input);

      // Хто надіслав / кому
      const senderId =
        res?.outMessage?.from_id?.user_id ?? res?.from_id?.user_id ?? res?.user_id ?? res?.userId;

      const peerId =
        res?.outMessage?.peer_id?.user_id ?? res?.peer_id?.user_id ?? res?.peer?.user_id;

      // не наш активний чат — ігноруємо
      if (activeId && senderId !== activeId && peerId !== activeId) return;

      // out === true → повідомлення від нас (адміна)
      const isOutgoing = Boolean(res?.userId || res?.user_id);

      const { messageText, type, mimeType, hash, fileName } = normalizePayloadFromRes(res);

      if (Number(res?.user_id) !== Number(id_user) && Number(res?.userId) !== Number(id_user)) {
        return;
      }

      // нове: витягуємо дані форварду (опційно)
      const fwdPayload = extractForward(res);

      dispatch(
        setMassage({
          admin: !isOutgoing, // від юзера => false
          message: messageText,
          type,
          mime_type: mimeType,
          hash: hash != null ? String(hash) : undefined,
          file_name: fileName,
          create_at: message?.data?.time,
          // додано форвард-поля (усі опційні; якщо нема фwd — не впливають)
          ...(fwdPayload as any),
        }),
      );
    };

    centrifuge.on('publication', handler);
    return () => {
      centrifuge.off?.('publication', handler);
    };
  }, [isAuth, token, ticket, ticketOpen, userData, dispatch, centrifuge, loadPage]);

  // 3) клік по юзеру
  const onOpenUser = (value: number, status: number | null = null, user_id: number) => {
    dispatch(removeListChats());
    setOpenUser(value === openUser ? 0 : value);
    dispatch(setTabSupport(value === openUser ? 0 : value));
    navigate({ search: `?${user_id}` });
    setId_user(user_id);
    dispatch(setStatus(status));

    if (value !== openUser) {
      dispatch(
        ticketsRequest({
          previous: nameTabSupport === 'Previos',
          current_page: 1,
          per_page: PAGE_SIZE,
          user_id,
        }),
      );
      dispatch(
        getUserRequest({
          user_id: String(users?.data.find((u) => u.support_id === value)?.user_id) || '',
        }),
      );
      dispatch(getNotesRequest(String(value)));
    }
  };

  // 4) зміна фільтрів з UI
  const onFilter = (values: IUsersRequest) => setFilter((prev) => ({ ...prev, ...values }));

  // 5) перший запит (mount)
  useEffect(() => {
    setPage(1);
    setBootstrapped(false);
    loadingRef.current = false;
    setIsLoading(false);
    prevQueryKeyRef.current = queryKey;
    prevLoadedRef.current = 0;
    setInitialLoading(true);
    lastRequestKeyRef.current = null; // <-- скинути анти-дубль
    scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    loadPage(1);
  }, []);

  // 6) якщо фільтри/вкладка змінилися → повний ресет і сторінка 1
  useEffect(() => {
    if (prevQueryKeyRef.current === null) return; // початкове встановлення відбулося вище
    if (prevQueryKeyRef.current !== queryKey) {
      setPage(1);
      setBootstrapped(false);
      loadingRef.current = false;
      setIsLoading(false);
      prevQueryKeyRef.current = queryKey;
      prevLoadedRef.current = 0;
      setInitialLoading(true);
      lastRequestKeyRef.current = null; // <-- скинути анти-дубль, щоб не ігнорувати новий 1-й запит
      // скрол вгору, щоб sentinel не був у видимості
      scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      loadPage(1);
    }
  }, [queryKey, loadPage]);

  // 7) коли дані підросли — зняти флаги + позначити, що перший фетч відбувся
  useEffect(() => {
    const grew = loadedCount > prevLoadedRef.current;
    if (initialLoading && loadedCount > 0) setInitialLoading(false);
    if (grew || !hasMore) {
      loadingRef.current = false;
      setIsLoading(false);
      if (!bootstrapped && loadedCount > 0) setBootstrapped(true);
      // після успішного отримання даних можна дозволити повтор того ж ключа у майбутньому
      // (але поки сторінка та фільтри ті ж — дубль не зʼявиться, бо loadingRef вже false)
      // lastRequestKeyRef залишаємо як є до наступного reset/change
    }
    prevLoadedRef.current = loadedCount;
  }, [loadedCount, hasMore, initialLoading, bootstrapped]);

  // 8) IntersectionObserver — підвантаження наступної сторінки
  useEffect(() => {
    if (!sentinelRef.current || !scrollRef.current) return;
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first.isIntersecting) return;
        if (!hasMore) return;
        if (loadingRef.current) return;
        if (!bootstrapped) return;

        const next = page + 1;
        setPage(next);
        loadPage(next);
      },
      { root, rootMargin: '0px 0px 200px 0px', threshold: 0.01 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, bootstrapped, page, loadPage]);

  // 9) Автофіл — якщо списку не вистачає для скролу
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const notScrollable = el.scrollHeight <= el.clientHeight;
    if (notScrollable && hasMore && !loadingRef.current) {
      if (!bootstrapped) return; // дочекайся 1-ї сторінки
      const next = page + 1;
      setPage(next);
      loadPage(next);
    }
  }, [loadedCount, hasMore, bootstrapped, page, loadPage]);

  if (!isAuth) return <Navigate to={ELinksName.LOGIN} state={{ from: location }} replace />;

  return (
    <div className="wrapper wrapper-support">
      <HeaderSuport
        setHideLeft={setHideLeft}
        setHideRight={setHideRight}
        hideLeft={hideLeft}
        hideRight={hideRight}
        openUser={openUser}
        id_user={id_user}
      />
      <main className="main">
        <section className="main-section">
          <SidebarSupportLeft
            setHideLeft={setHideLeft}
            hideLeft={hideLeft}
            activUser={openUser}
            onOpenUser={onOpenUser}
            users={users}
            onFilter={onFilter}
            onSaveChange={() => {
              /* фільтри застосовуються реактивно через queryKey */
            }}
            scrollRef={scrollRef}
            footer={
              <div
                style={{
                  width: '100%',
                  padding: '12px 0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {(isLoading || initialLoading) && <Loader />}
                <div ref={sentinelRef} style={{ height: 1, width: '100%' }} />
                {!hasMore && loadedCount > 0 && (
                  <div style={{ fontSize: 12, opacity: 0.6, marginTop: 8 }}>All results loaded</div>
                )}
              </div>
            }
          />
          {openUser ? (
            <Outlet context={{ centrifuge, getTicketOpen }} />
          ) : (
            <div className="main-content chats-background"></div>
          )}
          {openUser ? <SidebarSupportRight hideRight={hideRight} openUser={openUser} /> : null}
        </section>
      </main>
    </div>
  );
};

export default SuportLayout;

export function useSuport() {
  return useOutletContext<ContextType>();
}
